import { useEffect, useState } from "react";

import { TrendingUp, TrendingDown, Activity } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";


interface MomentumStats {
  high: number;
  medium: number;
  low: number;
  total: number;
}

interface MomentumOverviewProps {
  workspaceId: string;
}

export const MomentumOverview = ({ workspaceId }: MomentumOverviewProps) => {
  const [stats, setStats] = useState<MomentumStats>({ high: 0, medium: 0, low: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMomentumStats();
  }, [workspaceId]);

  const fetchMomentumStats = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc("get_workspace_momentum", {
        p_workspace_id: workspaceId,
      });

      if (error) throw error;

      const momentum = data || [];
      const counts = {
        high: momentum.filter((p: any) => p.momentum_level === "high").length,
        medium: momentum.filter((p: any) => p.momentum_level === "medium").length,
        low: momentum.filter((p: any) => p.momentum_level === "low").length,
        total: momentum.length,
      };

      setStats(counts);
    } catch (error) {
      console.error("Error fetching momentum stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  if (stats.total === 0) {
    return null;
  }

  const highPercentage = (stats.high / stats.total) * 100;
  const mediumPercentage = (stats.medium / stats.total) * 100;
  const lowPercentage = (stats.low / stats.total) * 100;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          Momentum Overview
        </CardTitle>
        <CardDescription>
          Activity status across {stats.total} project{stats.total !== 1 ? "s" : ""}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium">High Momentum</span>
            </div>
            <span className="text-sm tabular-nums text-muted-foreground">
              {stats.high} ({Math.round(highPercentage)}%)
            </span>
          </div>
          <div className="relative w-full h-2 bg-green-500/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-500 transition-all duration-300"
              style={{ width: `${highPercentage}%` }}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
              <span className="text-sm font-medium">Moderate Momentum</span>
            </div>
            <span className="text-sm tabular-nums text-muted-foreground">
              {stats.medium} ({Math.round(mediumPercentage)}%)
            </span>
          </div>
          <div className="relative w-full h-2 bg-yellow-500/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-yellow-500 transition-all duration-300"
              style={{ width: `${mediumPercentage}%` }}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400" />
              <span className="text-sm font-medium">Needs Attention</span>
            </div>
            <span className="text-sm tabular-nums text-muted-foreground">
              {stats.low} ({Math.round(lowPercentage)}%)
            </span>
          </div>
          <div className="relative w-full h-2 bg-red-500/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-red-500 transition-all duration-300"
              style={{ width: `${lowPercentage}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
